import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { format, parseISO, startOfWeek } from 'date-fns';
import { StyledSVG } from './GitHubHeatmap.styles';

function GitHubHeatmap({ contributions }) {
  const theme = useTheme();
  const [hoveredDay, setHoveredDay] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef(null);

  // Constants for rendering
  const CELL_SIZE = 19; // 19x19 square (20% larger than 16)
  const CELL_GAP = 7; // 3.5px padding on each side = 7px total gap
  const WEEK_WIDTH = CELL_SIZE + CELL_GAP; // 26px total
  const DAY_HEIGHT = CELL_SIZE + CELL_GAP; // 26px total
  const MONTH_LABEL_HEIGHT = 20;
  const LEGEND_HEIGHT = 30;
  const DAY_LABEL_WIDTH = 30;

  // Group contributions by date for quick lookup
  const contributionMap = useMemo(() => {
    const map = new Map();
    contributions.forEach(contrib => {
      map.set(contrib.date, contrib.count);
    });
    return map;
  }, [contributions]);

  // Find the date range
  const sortedContributions = useMemo(
    () =>
      [...contributions].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [contributions]
  );

  // Handle empty contributions array
  const { startDate, endDate } = useMemo(() => {
    if (sortedContributions.length === 0) {
      // Fallback to last year if no contributions
      const today = new Date();
      const oneYearAgo = new Date();
      oneYearAgo.setDate(oneYearAgo.getDate() - 365);
      return {
        startDate: oneYearAgo,
        endDate: today,
      };
    }
    return {
      startDate: parseISO(sortedContributions[0].date),
      endDate: parseISO(
        sortedContributions[sortedContributions.length - 1].date
      ),
    };
  }, [sortedContributions]);

  // Get all weeks in the range
  const weeks = useMemo(() => {
    const weeksArray = [];
    let currentWeekStart = startOfWeek(startDate, { weekStartsOn: 0 }); // Sunday

    while (currentWeekStart <= endDate) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const weekDays = [];
      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(currentWeekStart);
        dayDate.setDate(dayDate.getDate() + i);
        const dateStr = dayDate.toISOString().split('T')[0];
        const count = contributionMap.get(dateStr) || 0;
        weekDays.push({
          date: dateStr,
          dateObj: dayDate,
          count: count,
          weekday: dayDate.getDay(),
        });
      }

      weeksArray.push({
        start: new Date(currentWeekStart),
        days: weekDays,
      });

      currentWeekStart = new Date(currentWeekStart);
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }

    return weeksArray;
  }, [startDate, endDate, contributionMap]);

  // Find max contributions per day for color scaling
  const maxCount =
    contributions.length > 0
      ? Math.max(...contributions.map(c => c.count), 1)
      : 1;

  // Calculate month labels positions
  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = null;

    weeks.forEach((week, weekIndex) => {
      const firstDay = week.days[0].dateObj;
      const month = format(firstDay, 'MMM');

      if (month !== lastMonth) {
        labels.push({
          month,
          weekIndex,
        });
        lastMonth = month;
      }
    });

    return labels;
  }, [weeks]);

  // Get color based on contribution count
  // 5-level blue scale with high contrast and distinct 0 color
  const getColor = count => {
    if (count === 0) {
      return '#eef2f6'; // Distinct very light gray for 0 contributions
    }

    const intensity = count / maxCount;

    // High contrast blue gradient: 5 levels with better distinction
    // Using theme blue colors but with enhanced contrast between levels
    if (intensity <= 0.2) {
      return theme.palette.secondary.main; // #d3e1f3 - Light blue (lowest)
    } else if (intensity <= 0.4) {
      return theme.palette.secondary.dark; // #a8c4e8 - Medium-light blue
    } else if (intensity <= 0.6) {
      return theme.palette.primary.light; // #5a7a9a - Medium blue
    } else if (intensity <= 0.8) {
      return theme.palette.primary.main; // #344f6b - Dark blue
    } else {
      return theme.palette.primary.dark; // #1e2f3f - Darkest blue (highest)
    }
  };

  // Format date for tooltip
  const formatTooltipDate = dateStr => {
    const date = parseISO(dateStr);
    return format(date, 'MMM d, yyyy');
  };

  const SVG_LEFT_PADDING = 10;
  const svgWidth = weeks.length * WEEK_WIDTH + SVG_LEFT_PADDING;
  const svgHeight = 7 * DAY_HEIGHT + MONTH_LABEL_HEIGHT;

  const handleMouseEnter = (day, weekIndex, event) => {
    setHoveredDay({ date: day.date, weekIndex, day });
    const rect = event.currentTarget.getBoundingClientRect();

    // Position tooltip relative to viewport for fixed positioning
    setTooltipPosition({
      x: rect.left + rect.width / 2, // Center horizontally on the cell
      y: rect.top - 35, // Position above the cell
    });
  };

  const handleActivate = (day, weekIndex, event) => {
    // Handle both mouse clicks and keyboard activation (Enter/Space)
    handleMouseEnter(day, weekIndex, event);
  };

  const handleKeyDown = (day, weekIndex, event) => {
    // Activate on Enter or Space key
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate(day, weekIndex, event);
    }
  };

  const scrollToRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current.scrollWidth;
    }
  };

  useEffect(() => {
    scrollToRight();
  }, [weeks]);

  useEffect(() => {
    const handleResize = () => {
      scrollToRight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [weeks]);

  return (
    <Box
      sx={{
        position: 'relative',
        marginTop: theme.spacing(2),
      }}
    >
      {/* Tooltip rendered outside scrollable container with fixed positioning */}
      {hoveredDay && hoveredDay.day && (
        <Box
          sx={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateX(-50%)',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1),
            fontSize: '12px',
            boxShadow: theme.shadows[2],
            zIndex: 10000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <strong>{hoveredDay.day.count}</strong> contribution
          {hoveredDay.day.count !== 1 ? 's' : ''} on{' '}
          {formatTooltipDate(hoveredDay.day.date)}
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
        }}
      >
        {/* Day labels positioned outside scrollable area */}
        <Box
          sx={{
            width: `${DAY_LABEL_WIDTH}px`,
            flexShrink: 0,
            paddingTop: `${MONTH_LABEL_HEIGHT + 4}px`,
            backgroundColor: theme.palette.background.paper,
            pointerEvents: 'none',
          }}
        >
          {[
            { label: 'Sun', index: 0 },
            { label: 'Mon', index: 1 },
            { label: 'Tue', index: 2 },
            { label: 'Wed', index: 3 },
            { label: 'Thu', index: 4 },
            { label: 'Fri', index: 5 },
            { label: 'Sat', index: 6 },
          ].map(({ label }) => (
            <Box
              key={label}
              sx={{
                position: 'relative',
                height: `${DAY_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '5px',
                fontSize: '10px',
                color: theme.palette.text.secondary,
                lineHeight: 1,
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        <Box
          ref={scrollContainerRef}
          sx={{
            position: 'relative',
            overflowX: 'auto',
            flex: 1,
            paddingRight: '10px', // Prevent clipping of hover outlines on right edge
            paddingTop: '4px', // Prevent clipping of hover outlines on top edge
            paddingBottom: '4px', // Prevent clipping of hover outlines on bottom edge
          }}
        >
          <Box
            sx={{
              display: 'inline-block',
              marginTop: '4px',
              marginBottom: '4px',
            }}
          >
            <StyledSVG
              width={svgWidth}
              height={svgHeight}
              role="group"
              aria-label="GitHub contribution heatmap"
            >
              {/* Month labels at the top */}
              {monthLabels.map((label, index) => {
                const x = label.weekIndex * WEEK_WIDTH + 10; // Shift right to prevent clipping
                const nextLabel = monthLabels[index + 1];
                const width = nextLabel
                  ? (nextLabel.weekIndex - label.weekIndex) * WEEK_WIDTH
                  : (weeks.length - label.weekIndex) * WEEK_WIDTH;

                return (
                  <text
                    key={`${label.month}-${label.weekIndex}`}
                    x={x + width / 2}
                    y={15}
                    textAnchor="middle"
                    fontSize={10}
                    fill={theme.palette.text.secondary}
                  >
                    {label.month}
                  </text>
                );
              })}

              {/* Render weeks */}
              {weeks.map((week, weekIndex) => (
                <g
                  key={weekIndex}
                  transform={`translate(${weekIndex * WEEK_WIDTH + 10}, ${MONTH_LABEL_HEIGHT})`}
                >
                  {week.days.map((day, dayIndex) => {
                    const isHovered =
                      hoveredDay &&
                      hoveredDay.date === day.date &&
                      hoveredDay.weekIndex === weekIndex;

                    return (
                      <rect
                        key={`${weekIndex}-${dayIndex}`}
                        x={0}
                        y={dayIndex * DAY_HEIGHT}
                        width={CELL_SIZE}
                        height={CELL_SIZE}
                        fill={getColor(day.count)}
                        rx={2}
                        role="button"
                        className="github-heatmap-rect"
                        style={{
                          outline: isHovered
                            ? `2px solid ${theme.palette.primary.main}`
                            : 'none',
                        }}
                        onMouseEnter={e => handleMouseEnter(day, weekIndex, e)}
                        onMouseLeave={() => setHoveredDay(null)}
                        onClick={e => handleActivate(day, weekIndex, e)}
                        onKeyDown={e => handleKeyDown(day, weekIndex, e)}
                        aria-label={`${day.count} contributions on ${formatTooltipDate(day.date)}`}
                        tabIndex={0}
                        onFocus={e => handleMouseEnter(day, weekIndex, e)}
                        onBlur={() => setHoveredDay(null)}
                        data-date={day.date}
                      />
                    );
                  })}
                </g>
              ))}
            </StyledSVG>
          </Box>
        </Box>
      </Box>

      {/* Color scale legend - outside scrollable area */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginTop: '8px',
          height: `${LEGEND_HEIGHT}px`,
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '10px',
            color: theme.palette.text.secondary,
          }}
        >
          <span>Less</span>
          {/* Show 0 contributions color */}
          <Box
            sx={{
              width: `${CELL_SIZE}px`,
              height: `${CELL_SIZE}px`,
              backgroundColor: '#eef2f6',
              borderRadius: '2px',
            }}
          />
          {/* Show 5 intensity levels */}
          {[0, 1, 2, 3, 4].map((intensity, index) => {
            const count = Math.round(((intensity + 1) / 5) * maxCount);
            return (
              <Box
                key={index}
                sx={{
                  width: `${CELL_SIZE}px`,
                  height: `${CELL_SIZE}px`,
                  backgroundColor: getColor(count),
                  borderRadius: '2px',
                }}
              />
            );
          })}
          <span>More</span>
        </Box>
      </Box>
    </Box>
  );
}

export default GitHubHeatmap;
